import React, { createContext, useContext, useState, useMemo, useEffect, useRef, ReactNode, useCallback } from 'react';
import { useMetadata } from './metadata/main-metadata.js';
import { supabaseClient } from './supabase.ts';
import type { ProcessedMetadata } from './metadata/main-metadata.js';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useAuth } from './useAuth.ts';
import { startLiveSync } from './realtime/liveSync.ts';
import { LIVE_CHANGES_EVENT, LIVE_CHANGES_TOPIC, parseLiveChangeMessage, receiveLiveChange } from './realtime/liveChanges.ts';
import { getDisplayUserName } from './userDisplay.ts';

export type RealtimeStatus = 'CONNECTED' | 'DISCONNECTED' | 'RECONNECTING' | 'STALE';

export interface ConnectedUser {
  presence_ref: string;
  user_id?: number;
  email?: string;
  name?: string;
  online_at: string;
}

export interface RealtimeHealth {
  status: RealtimeStatus;
  lastEventTime: Date | null;
  errorCount: number;
  activeSubscriptions: number;
  connectedUsers: ConnectedUser[];
  reconnect: () => void;
  disconnectedAt: Date | null;
}

// Type for extended metadata with hashes
interface ExtendedMetadata {
  data: ProcessedMetadata | null;
  error: Error | null;
  isLoading: boolean;
  hashes: {
    clientSideSelectTablesHash: string;
    fieldDataHash: string;
    picklistHash: string;
    relationshipsHash: string;
    tableDataHash: string;
    uiComponentsHash: string;
  };
}

// Type for UI overlays context
interface UIOverlaysContextType {
  uiDataOverlays: Record<string, any>;
  setUiDataOverlays: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

// Type for provider props
interface ProviderProps {
  children: ReactNode;
}

const MetadataContext = createContext<ExtendedMetadata | undefined>(undefined);
// Connection health lives in its own context. It ticks on every realtime event,
// and folding it into the metadata value would hand every metadata consumer a
// new object on each DB change — which purges AG Grid's server-side row store.
const RealtimeHealthContext = createContext<RealtimeHealth | undefined>(undefined);
const UIOverlaysContext = createContext<UIOverlaysContextType | undefined>(undefined);

const LIVE_EVENT_MARK_INTERVAL_MS = 5_000;

export const MetadataProvider = ({ children }: ProviderProps) => {
  const metadata = useMetadata();
  const { appUser, user } = useAuth();
  const metadataData = metadata.data;
  
  // Realtime Health State
  const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>('DISCONNECTED');
  const [lastEventTime, setLastEventTime] = useState<Date | null>(null);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [subscriptionKey, setSubscriptionKey] = useState<number>(0); // Used to trigger reconnect
  const activeChannelRef = useRef<RealtimeChannel | null>(null);
  const presenceChannelRef = useRef<RealtimeChannel | null>(null);
  const [disconnectedAt, setDisconnectedAt] = useState<Date | null>(null);
  // Unique per tab/session — used as presence key so each connection is tracked independently
  const sessionId = useRef(Math.random().toString(36).substring(2, 10));

  // REG-963: a bulk write broadcasts several messages a second. Setting state on
  // each one re-rendered this provider every time; the health indicator and the
  // 60-second stale check only need the time to within a few seconds.
  const lastEventMarkRef = useRef(0);
  const markLiveEvent = useCallback(() => {
    const now = Date.now();
    if (now - lastEventMarkRef.current < LIVE_EVENT_MARK_INTERVAL_MS) return;
    lastEventMarkRef.current = now;
    setLastEventTime(new Date(now));
    setRealtimeStatus((current) => (current === 'CONNECTED' ? current : 'CONNECTED'));
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    setRealtimeStatus('RECONNECTING');
    setSubscriptionKey(prev => prev + 1);
  }, []);

  // Browser offline/online — instant detection for WiFi/adapter-level disconnects
  useEffect(() => {
    const handleOffline = () => setRealtimeStatus('DISCONNECTED');
    const handleOnline = () => reconnect();
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [reconnect]);

  const STALE_THRESHOLD_MS = 60_000;
  const AUTO_RECONNECT_MAX_DELAY_MS = 30_000;
  const reconnectAttempts = useRef(0);

  // Connection health polling — checks channel state + stale events every 5s
  useEffect(() => {
    const interval = setInterval(() => {
      const channel = activeChannelRef.current;
      // Check channel state directly (Phoenix socket heartbeat detects dead connections)
      if (channel && (channel as any).state !== 'joined') {
        if (realtimeStatus === 'CONNECTED' || realtimeStatus === 'STALE') {
          setRealtimeStatus('DISCONNECTED');
        }
        return;
      }
      // Check for stale connection (channel appears joined but no events flowing)
      if (realtimeStatus === 'CONNECTED' && lastEventTime && Date.now() - lastEventTime.getTime() > STALE_THRESHOLD_MS) {
        setRealtimeStatus('STALE');
      }
    }, 5_000);
    return () => clearInterval(interval);
  }, [realtimeStatus, lastEventTime]);

  // Track when we go offline for the warning banner
  useEffect(() => {
    if (realtimeStatus === 'CONNECTED') {
      setDisconnectedAt(null);
    } else if (!disconnectedAt && (realtimeStatus === 'DISCONNECTED' || realtimeStatus === 'STALE')) {
      setDisconnectedAt(new Date());
    }
  }, [realtimeStatus, disconnectedAt]);

  // Auto-reconnect with exponential backoff on DISCONNECTED, single attempt on STALE
  useEffect(() => {
    if (realtimeStatus === 'CONNECTED' || realtimeStatus === 'RECONNECTING') {
      if (realtimeStatus === 'CONNECTED') reconnectAttempts.current = 0;
      return;
    }
    if (realtimeStatus === 'STALE') {
      // Single reconnect attempt for stale — if it fails, poller will set DISCONNECTED
      // which then enters exponential backoff
      const timeout = setTimeout(() => reconnect(), 1000);
      return () => clearTimeout(timeout);
    }
    // DISCONNECTED — exponential backoff
    const delay = Math.min(1000 * 2 ** reconnectAttempts.current, AUTO_RECONNECT_MAX_DELAY_MS);
    const timeout = setTimeout(() => {
      reconnectAttempts.current += 1;
      reconnect();
    }, delay);
    return () => clearTimeout(timeout);
  }, [realtimeStatus, reconnect]);

    // REG-963: these walk every field and stringify every table. They ran on
    // every provider render, and the provider renders on realtime traffic, so
    // a bulk write cost every tab this work several times a second.
    const {
      clientSideSelectTablesHash, fieldDataHash, picklistHash,
      relationshipsHash, tableDataHash, uiComponentsHash,
    } = useMemo(() => {
      const updatedAtHash = (entries: Record<string, any> | undefined): string =>
        Object.keys(entries || {}).map((key) => `${key}:${entries?.[key]?.updated_at}`).join('');

      return {
        // "...Funder_178:2023-08-15T14:Funder_179:nullFunder_145:nullRep_36:null..."
        clientSideSelectTablesHash: updatedAtHash(metadataData?.clientSideSelectTables?.flattened),
        // "Submission_1:2024-02-29T18:00:00.000Z:Submission_2:2024-02-29T18:00:00.000Z..."
        fieldDataHash: updatedAtHash(metadataData?.fieldData?.flattened),
        picklistHash: updatedAtHash(metadataData?.picklistData?.flattened),
        relationshipsHash: updatedAtHash(metadataData?.relationshipLookupData?.flattened),
        // TODO add updated_at and proper columns then we can hash by updated_at
        tableDataHash: Object.keys(metadataData?.tableData?.flattened || {})
          .map((key) => `${key}:${JSON.stringify(metadataData?.tableData?.flattened[key])}`)
          .join(''),
        uiComponentsHash: updatedAtHash(metadataData?.uiComponents as Record<string, any> | undefined),
      };
    }, [metadataData]);

    const cachedValue: ExtendedMetadata = useMemo(() => {
      const extendedMetadata: ExtendedMetadata = {
        ...metadata,
        data: metadataData,
        hashes: {
          clientSideSelectTablesHash,
          fieldDataHash,
          picklistHash,
          relationshipsHash,
          tableDataHash,
          uiComponentsHash
        }
      };

      // todo remove this:
      (window as any).metadata = extendedMetadata.data;

      return extendedMetadata;
    }, [metadata, metadataData, clientSideSelectTablesHash, fieldDataHash, picklistHash, relationshipsHash, tableDataHash, uiComponentsHash]);

    const realtimeHealthValue: RealtimeHealth = useMemo(() => ({
      status: realtimeStatus,
      lastEventTime,
      errorCount,
      activeSubscriptions: supabaseClient.getChannels().length,
      connectedUsers,
      reconnect,
      disconnectedAt
    }), [realtimeStatus, lastEventTime, errorCount, connectedUsers, reconnect, disconnectedAt]);

    // Live changes channel: one private broadcast per database statement (see liveChanges.ts)
    useEffect(() => {
      if (activeChannelRef.current) {
        activeChannelRef.current.unsubscribe();
        supabaseClient.removeChannel(activeChannelRef.current);
        activeChannelRef.current = null;
      }
      // The channel is private: joining needs a signed-in session.
      if (!user?.id) return;

      setRealtimeStatus('RECONNECTING');
      startLiveSync();

      const handleLiveChange = ({ payload }: { payload: unknown }): void => {
        markLiveEvent();

        const message = parseLiveChangeMessage(payload);
        if (message) receiveLiveChange(message);
      };

      const liveChannel = supabaseClient
        .channel(LIVE_CHANGES_TOPIC, { config: { private: true } })
        .on('broadcast', { event: LIVE_CHANGES_EVENT }, handleLiveChange)
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            setRealtimeStatus('CONNECTED');
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setRealtimeStatus('DISCONNECTED');
            if (err) {
              console.error('Realtime live changes subscription error:', err);
              setErrorCount(prev => prev + 1);
            }
          }
        });

      activeChannelRef.current = liveChannel;

      return () => {
        liveChannel.unsubscribe();
        supabaseClient.removeChannel(liveChannel);
      };
    }, [subscriptionKey, user?.id, markLiveEvent]);

    // Presence channel — separate from DB changes for reliable cross-browser sync
    useEffect(() => {
      if (presenceChannelRef.current) {
        presenceChannelRef.current.unsubscribe();
        supabaseClient.removeChannel(presenceChannelRef.current);
      }

      const presenceChannel = supabaseClient
        .channel(`presence_${subscriptionKey}`, {
          // enabled:true is required by the Realtime server: without it the join succeeds
          // but no presence_state is ever delivered, so presenceState() stays empty and the
          // connected-users list is permanently blank. realtime-js 2.10.x doesn't send the
          // flag, hence the cast — drop it once the client sets it itself.
          config: { presence: { key: sessionId.current, enabled: true } as { key: string } },
        })
        .on('presence', { event: 'sync' }, () => {
          const state = presenceChannel.presenceState();
          const users: ConnectedUser[] = [];
          Object.values(state).forEach((presences: any) => {
            presences.forEach((p: any) => users.push(p));
          });
          setConnectedUsers(users);
        })
        .on('presence', { event: 'join' }, () => {
          setLastEventTime(new Date());
        })
        .on('presence', { event: 'leave' }, () => {
          setLastEventTime(new Date());
        })
        .subscribe(async (status, err) => {
          if (status === 'SUBSCRIBED') {
            const userId = appUser?.id || user?.id;
            const userEmail = appUser?.email || user?.email;
            // Same derivation as the edit-lock channel uses, so a user shows up under one
            // name everywhere in live collaboration.
            const userName = getDisplayUserName(appUser, user);

            const trackPayload = {
              online_at: new Date().toISOString(),
              ...(userId ? { user_id: userId, email: userEmail, name: userName } : {})
            };
            await presenceChannel.track(trackPayload);
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            if (err) console.error('Presence channel error:', err);
          }
        });

      presenceChannelRef.current = presenceChannel;

      return () => {
        presenceChannel.unsubscribe();
        supabaseClient.removeChannel(presenceChannel);
      };
    }, [subscriptionKey, appUser?.id, user?.id]);

  return (
    <MetadataContext.Provider value={cachedValue}>
      <RealtimeHealthContext.Provider value={realtimeHealthValue}>
        {children}
      </RealtimeHealthContext.Provider>
    </MetadataContext.Provider>
  );
};

/**
 * Hook to access global metadata throughout the application
 * @returns Extended metadata with hashes for change detection
 * @throws Error if used outside MetadataProvider
 */
export const useGlobalMetadata = (): ExtendedMetadata => {
  const context = useContext(MetadataContext);
  if (context === undefined) {
    throw new Error('useGlobalMetadata must be used within a MetadataProvider');
  }
  return context;
};

/**
 * Hook to access realtime connection health
 * @returns RealtimeHealth object
 * @throws Error if used outside MetadataProvider
 */
export const useRealtimeHealthContext = (): RealtimeHealth => {
  const context = useContext(RealtimeHealthContext);
  if (context === undefined) {
    throw new Error('useRealtimeHealthContext must be used within a MetadataProvider');
  }
  return context;
};

/**
 * Provider for UI overlay state management
 * @param children - React children components
 */
export const UIOverlaysProvider = ({ children }: ProviderProps) => {
  const [uiDataOverlays, setUiDataOverlays] = useState<Record<string, any>>({});
  return (
    <UIOverlaysContext.Provider value={{ uiDataOverlays, setUiDataOverlays }}>
      {children}
    </UIOverlaysContext.Provider>
  );
};

/**
 * Hook to access UI overlay state
 * @returns UI overlays context with state and setter
 * @throws Error if used outside UIOverlaysProvider
 */
export const useUIOverlays = (): UIOverlaysContextType => {
  const context = useContext(UIOverlaysContext);
  if (context === undefined) {
    throw new Error('useUIOverlays must be used within a UIOverlaysProvider');
  }
  return context;
};
